import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, MessageSquareText } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from 'components/ui/card';
import { Button } from 'components/ui/button';
import { Textarea } from 'components/ui/textarea';
import { useToast } from 'components/ui/toast';
import { useSaveOpsNote } from 'hooks/useTechnicians';
import { saveErrorMessage } from 'utils/errorMessage';
import { formatDate } from 'utils/formatDate';
import { OPS_NOTE_MAX_LENGTH, type OpsNote } from 'api/technicianImportContract';

interface OpsNoteSectionProps {
  technicianId: number;
  opsNote: OpsNote | null;
  canManage: boolean;
}

const NOTE_ID = 'technician-ops-note';

/** F33 — the ops comment, saved by its own route: saving the record never sends it (CA23). */
const OpsNoteSection = ({ technicianId, opsNote, canManage }: OpsNoteSectionProps) => {
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const saveNote = useSaveOpsNote();
  // null = reading; a string = the draft being typed.
  const [draft, setDraft] = useState<string | null>(null);
  const text = opsNote?.text ?? '';
  // `isPending` reaches the render a tick late; the ref closes the double-click window.
  const inFlight = useRef(false);
  const actionRef = useRef<HTMLButtonElement>(null);
  const wasEditing = useRef(false);

  useEffect(() => {
    // Leaving the editor (saved or cancelled) hands the focus back to Modifier / Ajouter.
    if (draft === null && wasEditing.current) actionRef.current?.focus();
    wasEditing.current = draft !== null;
  }, [draft]);

  const save = () => {
    if (draft === null || inFlight.current) return;
    inFlight.current = true;
    saveNote.mutate(
      { id: technicianId, text: draft.trim() },
      {
        onSuccess: () => {
          setDraft(null);
          toast.success(t('Comment saved'));
        },
        onError: (error) => toast.error(saveErrorMessage(t, error)),
        onSettled: () => {
          inFlight.current = false;
        },
      }
    );
  };

  const meta = opsNote
    ? opsNote.updatedBy
      ? t('Edited by {{name}} on {{date}}', { name: opsNote.updatedBy.name, date: formatDate(opsNote.updatedAt) })
      : t('Edited on {{date}}', { date: formatDate(opsNote.updatedAt) })
    : null;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <MessageSquareText className="h-4 w-4" aria-hidden="true" />
          {t('Ops comment')}
        </CardTitle>
        {canManage && draft === null && (
          <Button ref={actionRef} type="button" variant="outline" size="sm" onClick={() => setDraft(text)}>
            {text ? t('Edit') : t('Add')}
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {draft === null ? (
          <>
            <p
              className={
                text
                  ? 'whitespace-pre-wrap text-sm text-zinc-900 dark:text-zinc-50'
                  : 'text-sm text-zinc-500 dark:text-zinc-400'
              }
            >
              {text || t('No comment')}
            </p>
            {meta && <p className="text-xs text-zinc-500 dark:text-zinc-400">{meta}</p>}
          </>
        ) : (
          <>
            <Textarea
              id={NOTE_ID}
              value={draft}
              maxLength={OPS_NOTE_MAX_LENGTH}
              rows={4}
              autoFocus
              aria-label={t('Ops comment')}
              aria-describedby={`${NOTE_ID}-count`}
              onChange={(event) => setDraft(event.target.value)}
            />
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span id={`${NOTE_ID}-count`} className="text-xs tabular-nums text-zinc-500 dark:text-zinc-400">
                {t('{{length}} / {{max}} characters', {
                  length: draft.length,
                  max: new Intl.NumberFormat(i18n.language).format(OPS_NOTE_MAX_LENGTH),
                })}
              </span>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => !saveNote.isPending && setDraft(null)} aria-disabled={saveNote.isPending}>
                  {t('Cancel')}
                </Button>
                <Button type="button" onClick={save} aria-disabled={saveNote.isPending}>
                  {saveNote.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
                  {t('Save changes')}
                </Button>
              </div>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OpsNoteSection;
